import React, { useEffect, useMemo, useState } from 'react';

import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { MultiSelectOption, SelectOption } from '@rtkwlf/fenrir-react';
import { Field, size, VStack } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { DropdownSelect } from '@packages/components/dropdownSelect/DropdownSelect';
import { MultiSelector } from '@packages/components/dropdownSelect/MultiSelector';
import type { ItemType } from '@packages/components/dropdownSelect/SelectElements';
import { getInitialSelectedItems } from '@packages/components/filter/filter.utils';
import { DEBOUNCE_TIME_MEDIUM } from 'ae-constants/constants';

type FilterMenuProps = {
  isSingleSelect: boolean;
  field: string;
  titleKey?: string;

  /**
   * If options is set, it will be fed to DropdownSelect directly.
   * Otherwise, options are built based on the values prop below.
   */
  options?: ItemType[];
  values: string[];

  /**
   * If initialSelectedOption is set, it will be fed to DropdownSelect directly as selectedItem.
   * Otherwise, selectedItem are built based on the initialSelection.
   */
  initialSelectedOption?: ItemType[];

  handleChange: (
    field: string,
    option: SelectOption | MultiSelectOption[] | null,
    isSingleSelect?: boolean,
  ) => void;
  styles?: Record<string, SerializedStyles>;
  resetCounter?: number;
  initialSelection: Record<string, string[] | string>;
  handleTypeAhead?: (field: string, value: string) => void;
  isLoadingTypeAhead?: boolean;
  isErrorTypeAhead?: boolean;
  placeholderKey?: string;
};

export const FilterMenu: React.FC<FilterMenuProps> = ({
  isSingleSelect,
  field,
  titleKey,
  values,
  options,
  handleChange,
  handleTypeAhead,
  styles,
  resetCounter,
  initialSelection,
  initialSelectedOption,
  isLoadingTypeAhead,
  isErrorTypeAhead,
  placeholderKey,
}) => {
  const [selectedItems, setSelectedItems] = useState<ItemType[]>(
    initialSelectedOption ?? [],
  );
  const { t } = useTranslation();
  const debouncedTypeAheadHandler = useDebouncedCallback(
    (value: string) => handleTypeAhead?.(field, value),
    DEBOUNCE_TIME_MEDIUM,
  );

  useEffect(() => {
    let selectedItemsData;
    if (initialSelectedOption) {
      selectedItemsData = initialSelectedOption;
    } else {
      selectedItemsData = getInitialSelectedItems(initialSelection, field);
    }
    setSelectedItems(selectedItemsData);
  }, [initialSelection, field, initialSelectedOption]);

  const titleText = useMemo(
    () => (titleKey ? t(titleKey) : field.replace(/_/g, ' ')),
    [titleKey, field, t],
  );

  const items = useMemo(() => {
    if (options) {
      return options;
    }
    return values.map((val) => ({
      label: val,
      value: val,
    }));
  }, [options, values]);

  return isSingleSelect ? (
    <VStack gap="small" paddingTop="xsmall">
      {titleText && <Field.Label>{titleText}</Field.Label>}
      <DropdownSelect
        items={items}
        selectedItem={selectedItems.length > 0 ? selectedItems[0] : null}
        handleSelectedItemChange={(changes) => {
          handleChange(field, changes, isSingleSelect);
        }}
        style={css({
          minWidth: size['10rem'],
        })}
        placeholderKey={placeholderKey}
      />
    </VStack>
  ) : (
    <MultiSelector
      key={field}
      title={field}
      titleKey={titleKey}
      style={styles?.multiSelectInputWrapStyle}
      resetCounter={resetCounter}
      items={items}
      initialSelectedItems={getInitialSelectedItems(initialSelection, field)}
      handleSelectedItemsChange={(changes) => {
        handleChange(field, changes, false);
      }}
      handleFilterChange={debouncedTypeAheadHandler}
      isLoadingTypeAhead={isLoadingTypeAhead}
      isErrorTypeAhead={isErrorTypeAhead}
    />
  );
};
